import { isEqual, omit } from 'lodash-es'
import { LucidePin, LucidePinOff, LucideSlidersHorizontal } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMap } from 'react-map-gl'
import {
  aClient,
  aFiltersIsOpen,
  aFiltersPinned,
  aMapFilters,
  aParcelDataVisible,
  aSelectedGeographyLayerFilter,
  aVisibleGeographies,
  useAtom,
  useAtomValue,
} from '~/atoms'
import { PulseDot } from '~/components/common'
import ControlTooltip from '~/components/common/control-tooltip'
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/components/ui'
import { defaultFilters } from '~/constants'
import { useBreakpoint } from '~/hooks/browser'
import { cn } from '~/lib'
import {
  geographyLayerFilterOptions,
  type GeographyLayerFilterOption,
} from '~/lib/geography-layer-filter'
import type { Client } from '~/models'
import { useGeographies } from '~/hooks/map'
import {
  MapFeaturedFilter,
  MapGeographyLayerFilter,
  MapLabelsFilter,
  MapLeaseFilter,
  MapListingStatusFilter,
  MapLocationFilter,
  MapPropertyFilter,
  MapPropertyTypesFilter,
  MapRailFilter,
  MapSaleFilter,
  MapSort,
  MapTypeFilter,
} from './filters'

function getObjectDiff(obj1: Record<string, any>, obj2: Record<string, any>) {
  const diff = Object.keys(obj1).reduce((result, key) => {
    if (!obj2.hasOwnProperty(key)) {
      result.push(key)
    } else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key)
      result.splice(resultKeyIndex, 1)
    }
    return result
  }, Object.keys(obj2))

  return diff
}

function runInterval(callback: () => void, interval: number, times: number) {
  let counter = 0
  const timer = setInterval(() => {
    if (counter >= times) {
      clearInterval(timer)
    } else {
      callback()
      counter += 1
    }
  }, interval)
}

function showLabelsFilter(client: Client): boolean {
  if (
    !client.labels ||
    client.labels.length === 0 ||
    client.listing_labels === undefined ||
    Object.keys(client.listing_labels).length === 0
  ) {
    return false
  }

  const idsToMatch = client.labels.map((label) => label.id)
  const matchingKeys = Object.values(client.listing_labels).filter((ids) =>
    idsToMatch.some((id) => ids.includes(id))
  )

  return matchingKeys.length > 0
}

export default function FilterSheet() {
  const [mapFilters, setMapFilters] = useAtom(aMapFilters)
  const [filters, setFilters] = useState(mapFilters || {})
  const client = useAtomValue(aClient)
  const {
    geographies,
    fileGeographies,
    isLoading: geographiesLoading,
  } = useGeographies(client!)
  const geographyLayerOptions = useMemo(
    () => geographyLayerFilterOptions(geographies, fileGeographies),
    [geographies, fileGeographies]
  )
  const [selectedGeographyLayer, setSelectedGeographyLayer] = useAtom(
    aSelectedGeographyLayerFilter
  )
  const [geographyLayerDraft, setGeographyLayerDraft] =
    useState<GeographyLayerFilterOption | null>(selectedGeographyLayer)
  const [, setVisibleGeographies] = useAtom(aVisibleGeographies)
  const [, setParcelDataVisible] = useAtom(aParcelDataVisible)
  const showLabels = useMemo(() => showLabelsFilter(client!), [client])

  const [isOpen, setIsOpen] = useAtom(aFiltersIsOpen)
  const [isPinned, setIsPinned] = useAtom(aFiltersPinned)
  const { lgDown } = useBreakpoint()
  const mapRef = useMap()

  const toggleSheetPinned = () => {
    setIsPinned(!isPinned)
  }

  useEffect(() => {
    const mapContainer = document.getElementById('map-container')
    if (mapContainer) {
      if (isPinned) {
        mapContainer.style.paddingRight = '24rem'
      } else {
        mapContainer.style.paddingRight = '0'
      }
      runInterval(
        () => {
          mapRef.current!.triggerRepaint()
          mapRef.current!.resize()
        },
        50,
        5
      )
    }
  }, [isPinned])

  useEffect(() => {
    if (lgDown && isPinned) {
      setIsPinned(false)
    }
  }, [lgDown, isPinned])

  useEffect(() => {
    if (!isOpen) {
      setFilters(mapFilters)
      setGeographyLayerDraft(selectedGeographyLayer)
    }
  }, [mapFilters, isOpen, selectedGeographyLayer, setFilters])

  useEffect(() => {
    if (geographiesLoading || !selectedGeographyLayer) return

    const isAvailable = geographyLayerOptions.some(
      (option) => option.id === selectedGeographyLayer.id
    )
    if (!isAvailable) {
      setSelectedGeographyLayer(null)
      setGeographyLayerDraft((draft) =>
        draft?.id === selectedGeographyLayer.id ? null : draft
      )
    }
  }, [
    geographiesLoading,
    geographyLayerOptions,
    selectedGeographyLayer,
    setSelectedGeographyLayer,
  ])

  useEffect(() => {
    if (geographiesLoading) return

    const urlOption = mapFilters.geography
      ? geographyLayerOptions.find(
          (option) => option.id === mapFilters.geography
        )
      : null
    if (!urlOption) {
      if (selectedGeographyLayer) {
        setSelectedGeographyLayer(null)
      }
      if (!isOpen) {
        setGeographyLayerDraft(null)
      }
      return
    }

    if (selectedGeographyLayer?.id !== urlOption.id) {
      setSelectedGeographyLayer(urlOption)
    }
    if (!isOpen) {
      setGeographyLayerDraft(urlOption)
    }
  }, [
    geographiesLoading,
    geographyLayerOptions,
    isOpen,
    mapFilters.geography,
    selectedGeographyLayer?.id,
    setSelectedGeographyLayer,
  ])

  const updateFilter = useCallback(
    (key: string, value: any) => {
      const newFilters: { [key: string]: any } = { ...filters }
      if (typeof value === 'undefined') {
        delete newFilters[key]
      } else {
        newFilters[key] = value
      }
      setFilters(newFilters)
    },
    [filters]
  )

  const updateMapFilters = () => {
    if ((filters.types || []).includes('sale') === false) {
      delete filters.min_price
      delete filters.max_price
      delete filters.min_cap
      delete filters.max_cap
    }

    if ((filters.types || []).includes('lease') === false) {
      delete filters.min_rate
      delete filters.max_rate
      delete filters.min_sf
      delete filters.max_sf
    }

    setMapFilters({
      ...filters,
      geography: geographyLayerDraft?.id,
    })
    setSelectedGeographyLayer(geographyLayerDraft)
    if (geographyLayerDraft) {
      setParcelDataVisible(false)
      setVisibleGeographies((visible) =>
        visible.includes(geographyLayerDraft.id)
          ? visible
          : [...visible, geographyLayerDraft.id]
      )
    }
    if (!isPinned) {
      setIsOpen(false)
    }
  }

  const resetMapFilters = () => {
    const filters = {
      lat: mapFilters.lat,
      lng: mapFilters.lng,
      z: mapFilters.z,
    }
    setMapFilters(filters)
    setSelectedGeographyLayer(null)
    setGeographyLayerDraft(null)
    setTimeout(() => setFilters({ ...filters, ...defaultFilters }), 200)
    setIsOpen(false)
  }
  const different = useMemo(() => {
    return (
      getObjectDiff(
        omit(mapFilters, ['lat', 'lng', 'z']),
        omit(defaultFilters, ['lat', 'lng', 'z'])
      ).length > 0 || Boolean(selectedGeographyLayer)
    )
  }, [mapFilters])

  return (
    <Sheet
      modal={false}
      open={isPinned || isOpen}
      onOpenChange={(open) => {
        if (!isPinned) {
          setIsOpen(open)

          if (!open) {
            setFilters(mapFilters)
          }
        }
      }}>
      {!isPinned && (
        <ControlTooltip text="Filter and sort listings" side="bottom">
          <SheetTrigger asChild>
            <Button variant="map" className="relative px-3 shadow-md">
              <LucideSlidersHorizontal className="h-4 w-4" />
              <span className="ml-2">Filters</span>
              {different && (
                <PulseDot className="absolute -right-0.5 -top-0.5" />
              )}
            </Button>
          </SheetTrigger>
        </ControlTooltip>
      )}
      <SheetContent
        className={cn('flex w-full flex-col', isPinned && '!duration-0')}
        side="right"
        onCloseClick={() => {
          setIsOpen(false)
          setIsPinned(false)
        }}>
        <SheetHeader>
          <SheetTitle>
            Filter Listings{' '}
            <Button
              variant="link"
              size="none"
              className="p-0"
              onClick={() => {
                resetMapFilters()
              }}>
              (reset all)
            </Button>
          </SheetTitle>
          <button
            className="absolute right-11 top-2 hidden rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary xl:block"
            onClick={() => toggleSheetPinned()}>
            {isPinned ? (
              <LucidePinOff className="h-4 w-4" />
            ) : (
              <LucidePin className="h-4 w-4" />
            )}
            <span className="sr-only">Pin Filters</span>
          </button>
          <SheetDescription className="sr-only"></SheetDescription>
        </SheetHeader>
        <div className="-mx-6 flex-1 overflow-auto px-6">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              updateMapFilters()
            }}>
            <div className="space-y-4 pb-2">
              {client!.featured && client!.featured.length > 0 && (
                <MapFeaturedFilter
                  featured={filters.featured}
                  updateFilter={updateFilter}
                />
              )}

              {!geographiesLoading && (
                <MapGeographyLayerFilter
                  options={geographyLayerOptions}
                  value={geographyLayerDraft}
                  updateFilter={setGeographyLayerDraft}
                />
              )}

              {showLabels && (
                <MapLabelsFilter
                  clientLabels={client!.labels!}
                  labels={filters.labels || []}
                  updateFilter={updateFilter}
                />
              )}

              <MapSort sort={filters.sort} updateFilter={updateFilter} />

              <MapPropertyTypesFilter
                propertyTypes={filters.property_types || []}
                updateFilter={updateFilter}
              />

              <MapListingStatusFilter
                statuses={filters.statuses || []}
                updateFilter={updateFilter}
              />

              <MapTypeFilter
                types={filters.types || []}
                updateFilter={updateFilter}
              />

              {filters?.types?.includes('sale') && (
                <MapSaleFilter
                  maxPrice={filters.max_price}
                  minPrice={filters.min_price}
                  maxCap={filters.max_cap}
                  minCap={filters.min_cap}
                  updateFilter={updateFilter}
                />
              )}

              {filters?.types?.includes('lease') && (
                <MapLeaseFilter
                  maxRate={filters.max_rate}
                  minRate={filters.min_rate}
                  maxSf={filters.max_sf}
                  minSf={filters.min_sf}
                  updateFilter={updateFilter}
                />
              )}

              <MapPropertyFilter
                maxAcres={filters.max_acres}
                minAcres={filters.min_acres}
                maxBsf={filters.max_bsf}
                minBsf={filters.min_bsf}
                updateFilter={updateFilter}
              />
              <MapRailFilter rail={filters.rail} updateFilter={updateFilter} />

              <MapLocationFilter
                cities={filters.cities || []}
                counties={filters.counties || []}
                zipCodes={filters.zipCodes || []}
                updateFilter={updateFilter}
              />
            </div>
            <input className="hidden" type="submit" />
          </form>
        </div>
        <div className="flex gap-4 border-t pt-6">
          <Button
            onClick={() => {
              updateMapFilters()
            }}>
            Update
          </Button>
          {!isPinned && (
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
